import { cn } from "@dl/web-core-js/ui";

export type ProgressTone = "idle" | "active" | "done" | "skipped" | "warn" | "risk";

const FILL: Record<ProgressTone, string> = {
  idle: "bg-ink-faint",
  active: "bg-info",
  done: "bg-ok",
  skipped: "bg-line",
  warn: "bg-warn",
  risk: "bg-risk",
};

/**
 * A thin bar for "how far along": a lot, a component, a step. The value is a
 * share from 0 to 1 (clamped); the tone says what the colour means, so a
 * stalled step can be amber at 40% and a done one green at 100%.
 */
export function ProgressBar({
  value,
  tone = "active",
  className,
  label,
}: {
  value: number | null | undefined;
  tone?: ProgressTone;
  className?: string;
  /** For screen readers; the visible figure is the caller's. */
  label?: string;
}) {
  const share = Math.max(0, Math.min(1, Number(value ?? 0)));
  return (
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(share * 100)}
      aria-label={label}
      className={cn("h-1.5 w-full overflow-hidden rounded-full bg-surface-sunk", className)}
    >
      <div
        className={cn(
          "h-full rounded-full transition-[width]",
          FILL[tone],
          tone === "skipped" && "bg-[repeating-linear-gradient(45deg,var(--line)_0_3px,transparent_3px_6px)]",
        )}
        style={{ width: `${String(tone === "skipped" ? 100 : share * 100)}%` }}
      />
    </div>
  );
}

/** "42%" for a 0..1 share; empty for nothing known. */
export function percent(value: number | null | undefined): string {
  return value == null ? "" : `${String(Math.round(Math.max(0, Math.min(1, value)) * 100))}%`;
}
